import PropTypes from 'prop-types';
import React from 'react';
import { useStore } from 'react-redux';
import { useIntl } from 'react-intl';

import {
  Button,
  Table,
  convertToString,
  convertToTimeString,
  isSameDayDate
} from 'groundkeeper-component-library';

import EmptyCell from './EmptyCell';
import jsonStrings from 'shared/SharedJS/src/utils/intl/jsonStrings';
import selectUnitById from 'shared/Redux/selectors/unit/selectUnitById';
import { isoDateSort } from 'shared/Redux/utils/isoDateSort';

// eslint-disable-next-line react/display-name
const DateTable = React.forwardRef((props, ref) => {
  const intl = useIntl();
  const store = useStore();

  const columns = [
    {
      label: intl.formatMessage({
        id: 'date',
        defaultMessage: 'Date'
      }),
      render: c => `
        ${convertToString(new Date(c.start), {
          withTime: false,
          withYear: true
        })}${
          isSameDayDate(new Date(c.start), new Date(c.end))
            ? ''
            : ` - ${convertToString(new Date(c.end), {
                withTime: false,
                withYear: true
              })}`
        }`
    },
    {
      label: `${intl.formatMessage({
        id: 'public_holidays',
        defaultMessage: 'Public holidays'
      })}/${intl.formatMessage({
        id: 'holidays',
        defaultMessage: 'Holidays'
      })}`,
      render: c =>
        []
          .concat((c.public_holiday_conflicts || []).map(ph => ph.name))
          .concat((c.holiday_conflicts || []).map(h => h.name))
          .filter(s => !!s)
          .join(', ') || <EmptyCell align="left" />
    },
    {
      label: intl.formatMessage({
        id: 'start_end_time',
        defaultMessage: 'Start-/End time'
      }),
      render: c =>
        `${convertToTimeString(new Date(c.start)).pretty} - ${
          convertToTimeString(new Date(c.end)).pretty
        }`
    },
    {
      label: intl.formatMessage({
        id: 'units',
        defaultMessage: 'Units'
      }),
      render: c =>
        c.units
          .map(u => selectUnitById(u.id)(store.getState())?.name)
          .join(', ')
    },
    {
      label: intl.formatMessage({
        id: 'label',
        defaultMessage: 'Label'
      }),
      render: c =>
        c.type === 'closure'
          ? intl.formatMessage({
              id: 'blocked',
              defaultMessage: 'Blocked'
            })
          : intl.formatMessage({
              id: 'occupied',
              defaultMessage: 'Occupied'
            })
    },
    {
      label: intl.formatMessage({
        id: 'position',
        defaultMessage: 'Position'
      }),
      render: c => c.position
    },
    {
      label: intl.formatMessage({
        id: 'occupancy_type',
        defaultMessage: 'Occupancy type'
      }),
      render: c => {
        if (c.season_id) {
          return c.type === 'closure'
            ? intl.formatMessage({
                id: 'closure_permanent',
                defaultMessage: 'Permanent occupancy'
              })
            : intl.formatMessage({
                id: 'occupancy_permanent',
                defaultMessage: 'Permanent occupancy'
              });
        } else if (c.holiday_id) {
          return intl.formatMessage({
            id: 'occupancy_holiday',
            defaultMessage: 'Holiday occupancy'
          });
        } else {
          return c.type === 'closure'
            ? intl.formatMessage({
                id: 'closure_single',
                defaultMessage: 'Permanent single'
              })
            : intl.formatMessage({
                id: 'occupancy_single',
                defaultMessage: 'Single occupancy'
              });
        }
      }
    },
    {
      label: intl.formatMessage({
        id: 'occupancy_activity',
        defaultMessage: 'Activity'
      }),
      render: c => {
        if (c.type === 'closure') {
          return jsonStrings[`closure_type_${c.closure_type}`] ? (
            intl.formatMessage(jsonStrings[`closure_type_${c.closure_type}`])
          ) : (
            <EmptyCell />
          );
        } else {
          return jsonStrings[c.type] ? (
            intl.formatMessage(jsonStrings[c.type])
          ) : (
            <EmptyCell />
          );
        }
      }
    },
    {
      label: intl.formatMessage({
        id: 'actions',
        defaultMessage: 'Actions'
      }),
      labelHidden: true,
      render: c => (
        <Button
          aira-label={intl.formatMessage({
            id: 'show_more',
            defaultMessage: 'Show more'
          })}
          icon
          onClick={() => props.handleElementClick(c)}
        >
          info-circle
        </Button>
      )
    }
  ];

  const sortedDates = props.dates?.length
    ? props.dates.sort((a, b) => isoDateSort(a, b, 'start'))
    : [];

  return (
    <>
      <Table
        id={props.id}
        ref={ref}
        loading={props.loading}
        fullWidth
        shadowHead
        data-testid="dates-table"
        columns={columns}
        rows={sortedDates}
      />
    </>
  );
});

DateTable.propTypes = {
  id: PropTypes.string,
  archived: PropTypes.bool,
  loading: PropTypes.bool,
  dates: PropTypes.arrayOf(PropTypes.object),
  holidays: PropTypes.arrayOf(PropTypes.object),
  publicHolidays: PropTypes.arrayOf(PropTypes.object),
  handleSortChange: PropTypes.func,
  setOpenRestoreModal: PropTypes.func,
  setRestoreElement: PropTypes.func,
  handleElementClick: PropTypes.func
};

DateTable.defaultProps = {
  id: null,
  archived: false,
  loading: false,
  dates: [],
  handleSortChange: null,
  setOpenRestoreModal: null,
  setRestoreElement: null
};

export default DateTable;
